import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ServiceTask } from './service-task';
export { ServiceTask };
import { serviceTaskSchema } from '@schemas/business-objects/service-task';
import { ServiceTaskHistoryFormArray } from './service-task-history-form-group';
import { TenantFormGroup } from './tenant-form-group';
import { TokenFormGroup } from './token-form-group';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessInstance } from './process-instance';
import type { ProcessInstanceHistory } from './process-instance-history';

export class ServiceTaskFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    name: SchemaFormControl;
    queue: SchemaFormControl;
    status: SchemaFormControl;
    attempts: SchemaFormControl;
    claimedBy: SchemaFormControl;
    claimedAt: SchemaFormControl;
    errorMessage: SchemaFormControl;
    errorCode: SchemaFormControl;
    data: SchemaFormControl;
    completedAt: SchemaFormControl;
    loopIndex: SchemaFormControl;
    resultApplied: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    tokenId: SchemaFormControl;
    token: TokenFormGroup;
    serviceTaskHistories: ServiceTaskHistoryFormArray;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<ServiceTask>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(serviceTaskSchema, ServiceTask, data, options);
  }

  get object(): ServiceTask {
    return new ServiceTask(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'token':
        return new TokenFormGroup();
      case 'serviceTaskHistories':
        return new ServiceTaskHistoryFormArray();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ServiceTask[]> {
    return ServiceTask.find(filter);
  }

  static async findOne(filter?: any): Promise<ServiceTask | null> {
    return ServiceTask.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<ServiceTask> {
    return ServiceTask.findById(id, filter);
  }

  static async create(data: Partial<ServiceTask>): Promise<ServiceTask> {
    return ServiceTask.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<ServiceTask>,
  ): Promise<ServiceTask> {
    return ServiceTask.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return ServiceTask.deleteById(id);
  }

  static async claimNext(options: any): Promise<any> {
    return ServiceTask.claimNext(options);
  }

  async claim(options: any): Promise<any> {
    const instance = new ServiceTask(this.getRawValue());
    return instance.claim(options);
  }

  async complete(options: any): Promise<any> {
    const instance = new ServiceTask(this.getRawValue());
    return instance.complete(options);
  }

  async error(options: any): Promise<any> {
    const instance = new ServiceTask(this.getRawValue());
    return instance.error(options);
  }

  async fail(options: any): Promise<any> {
    const instance = new ServiceTask(this.getRawValue());
    return instance.fail(options);
  }
}

export class ServiceTaskFormArray extends PersistedFormArray {
  declare controls: ServiceTaskFormGroup[];

  readonly entityName = 'ServiceTask' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(serviceTaskSchema, ServiceTask, options);
  }

  protected override createItemGroup() {
    return new ServiceTaskFormGroup();
  }

  get array(): ServiceTask[] {
    return this.controls.map((group: ServiceTaskFormGroup) => group.object);
  }
}

export class ServiceTaskPersistedArray extends PersistedArray<ServiceTask> {
  readonly entityName = 'ServiceTask' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ServiceTask, options);
  }
}
